import {
  SitecoreProvider,
  ComponentPropsContext,
  ErrorPage,
  getSiteRewrite,
} from '@sitecore-content-sdk/nextjs';
import { SitecorePageProps } from 'lib/page-props';
import components from '.sitecore/component-map';
import Layout from 'src/Layout';
import { GetStaticProps } from 'next';
import { siteResolver } from 'lib/site-resolver';
import client from 'lib/sitecore-client';
import scConfig from 'sitecore.config';
import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import {
  getBuildErrorPageSiteEntries,
  fetchErrorPageProps,
  getCachedErrorPageProps,
  getErrorPageSiteKey,
} from 'lib/utils/error-page-utils';

/**
 * Rendered in case if we have 500 error
 */
export const DefaultServerError = (): JSX.Element => (
  <>
    <Head>
      <title>500: Server Error</title>
    </Head>
    <div style={{ padding: 10 }}>
      <h1>500 Internal Server Error</h1>
      <p>There is a problem with the resource you are looking for, and it cannot be displayed.</p>
      <a href="/">Go to the Home page</a>
    </div>
  </>
);

type ErrorPageMap = Record<string, SitecorePageProps | null>;

const Custom500 = (props: { resultErrorPages?: ErrorPageMap }): JSX.Element => {
  const router = useRouter();
  const [pageProps, setPageProps] = useState<SitecorePageProps | null | 'loading'>('loading');

  // We need to determine the site name client-side because NextJS doesn't provide the ability
  // to determine which site we're on due to middleware rewrites not executing for the 500 handler.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    let isActive = true;
    const domain = window.location.hostname;
    let siteKey: string;
    let siteName: string;
    let locale: string;
    try {
      const info = siteResolver.getByHost(domain);
      siteKey = getErrorPageSiteKey({
        siteName: info.name,
        resolvedSiteLocale: info.language || scConfig.defaultLanguage,
        routeLocale: typeof router.locale === 'string' ? router.locale : undefined,
        path: router.asPath || window.location.pathname,
      });
      [siteName, locale] = siteKey.split('__');
    } catch {
      setPageProps(null);
      return;
    }

    setPageProps('loading');
    const preBuiltProps = props.resultErrorPages?.[siteKey] ?? null;
    if (preBuiltProps) {
      setPageProps(preBuiltProps);
      return;
    }

    const cachedPageProps = getCachedErrorPageProps({ errorType: '500', siteName, locale });
    if (cachedPageProps !== undefined) {
      setPageProps(cachedPageProps);
    } else {
      setPageProps('loading');
    }

    fetchErrorPageProps({ errorType: '500', siteName, locale })
      .then((errorPageProps) => {
        if (isActive) {
          setPageProps(errorPageProps);
        }
      })
      .catch(() => {
        if (isActive) {
          setPageProps(null);
        }
      });

    return () => {
      isActive = false;
    };
  }, [router.asPath, router.locale, props.resultErrorPages]);

  if (pageProps === 'loading') {
    return <DefaultServerError />;
  }
  if (!pageProps || !pageProps.page) {
    return <DefaultServerError />;
  }
  return (
    <ComponentPropsContext value={pageProps.componentProps || {}}>
      <SitecoreProvider componentMap={components} api={scConfig.api} page={pageProps.page}>
        <Layout page={pageProps.page} headLinks={pageProps.headLinks} />
      </SitecoreProvider>
    </ComponentPropsContext>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const resultErrorPages: ErrorPageMap = {};
  const entries = getBuildErrorPageSiteEntries(
    context.defaultLocale || scConfig.defaultLanguage,
    scConfig.defaultSite,
    true
  );

  for (const entry of entries) {
    try {
      const rawPage = await client.getErrorPage(ErrorPage.InternalServerError, {
        site: entry.siteName,
        locale: entry.locale,
      });
      const pagePath = rawPage?.layout?.sitecore?.context?.itemPath;
      if (!pagePath) {
        resultErrorPages[`${entry.siteName}__${entry.locale}`] = null;
        continue;
      }

      const siteEncodedPath = getSiteRewrite(pagePath, { siteName: entry.siteName });
      const pageProps = await sitecorePagePropsFactory.create({
        ...context,
        locale: entry.locale,
        defaultLocale: entry.locale,
        params: { path: siteEncodedPath.split('/') },
        preview: false,
      });

      resultErrorPages[`${entry.siteName}__${entry.locale}`] = pageProps.page?.layout?.sitecore
        ?.route
        ? pageProps
        : null;
    } catch (error) {
      console.error(`Error fetching 500 page for ${entry.siteName} (${entry.locale}):`, error);
      resultErrorPages[`${entry.siteName}__${entry.locale}`] = null;
    }
  }

  const serialized = JSON.stringify({ resultErrorPages });
  console.log(
    `[500] static entries: ${entries.length}; payload: ${Buffer.byteLength(serialized)} bytes`
  );

  return { props: JSON.parse(serialized) };
};

export default Custom500;
